import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Eye,
  TrendingUp,
  Users,
  Search,
  Clock,
  IndianRupee,
  ArrowUpRight,
} from "lucide-react";
import { useLang } from "../contexts/LanguageContext";

// Local copy keeps i18n self-contained without bloating translations file
const COPY = {
  en: {
    eyebrow: "Why It Matters",
    title: "What Launch Mitra unlocks for your business.",
    subtitle:
      "Real outcomes you'll feel within weeks — more eyes, more trust, more customers walking in.",
    cards: [
      { t: "More Visibility", d: "Show up on Google when locals search for your business. Look professional 24/7.", m: "+3.2x", ms: "search impressions in 60 days" },
      { t: "Stronger Brand", d: "A logo, colors and a website that make customers say 'this place looks premium'.", m: "92%", ms: "say brand looks more trustworthy" },
      { t: "Online Bookings", d: "Let customers WhatsApp, call or book directly from your site — even at 11 PM.", m: "24/7", ms: "always-open lead capture" },
      { t: "Social Reach", d: "Scroll-stopping Instagram & Facebook posts that bring new customers every week.", m: "+62%", ms: "average follower growth" },
      { t: "Save Months", d: "Launch in days, not months. AI does the heavy lifting; we polish every detail.", m: "5 days", ms: "average go-live time" },
      { t: "Save Lakhs", d: "Agency-quality output without agency-sized invoices. Start at just ₹4,999.", m: "70%", ms: "cheaper than traditional agencies" },
    ],
  },
  hi: {
    eyebrow: "यह क्यों मायने रखता है",
    title: "Launch Mitra आपके बिज़नेस के लिए क्या खोलता है।",
    subtitle: "हफ़्तों में महसूस होने वाले असली नतीजे — ज़्यादा नज़रें, ज़्यादा भरोसा, ज़्यादा ग्राहक।",
    cards: [
      { t: "ज़्यादा विज़िबिलिटी", d: "जब लोग Google पर खोजें, तो आपका बिज़नेस सबसे आगे दिखे। 24/7 प्रोफ़ेशनल लगें।", m: "+3.2x", ms: "60 दिनों में सर्च इंप्रेशन" },
      { t: "मज़बूत ब्रांड", d: "लोगो, रंग और वेबसाइट जो ग्राहकों को कहने पर मजबूर करें — 'यह तो प्रीमियम है'।", m: "92%", ms: "ब्रांड ज़्यादा भरोसेमंद लगता है" },
      { t: "ऑनलाइन बुकिंग", d: "ग्राहक WhatsApp, कॉल या साइट से सीधे बुक करें — रात 11 बजे भी।", m: "24/7", ms: "हमेशा खुली लीड कैप्चर" },
      { t: "सोशल रीच", d: "Instagram और Facebook पोस्ट्स जो हर हफ़्ते नए ग्राहक लाएँ।", m: "+62%", ms: "औसत फॉलोअर ग्रोथ" },
      { t: "महीनों की बचत", d: "महीनों नहीं, दिनों में लॉन्च। AI भारी काम करता है, हम हर डिटेल चमकाते हैं।", m: "5 दिन", ms: "औसत लॉन्च समय" },
      { t: "लाखों की बचत", d: "एजेंसी जैसी क्वालिटी, बिना एजेंसी के बिल। मात्र ₹4,999 से शुरू।", m: "70%", ms: "एजेंसियों से सस्ता" },
    ],
  },
};

const ICONS = [Eye, TrendingUp, Users, Search, Clock, IndianRupee];

const TINTS = [
  { glow: "from-blue-500/40 to-cyan-400/10", chip: "text-blue-300", ring: "ring-blue-400/30" },
  { glow: "from-violet-500/40 to-fuchsia-400/10", chip: "text-violet-300", ring: "ring-violet-400/30" },
  { glow: "from-emerald-500/40 to-teal-400/10", chip: "text-emerald-300", ring: "ring-emerald-400/30" },
  { glow: "from-rose-500/40 to-pink-400/10", chip: "text-rose-300", ring: "ring-rose-400/30" },
  { glow: "from-amber-500/40 to-orange-400/10", chip: "text-amber-300", ring: "ring-amber-400/30" },
  { glow: "from-sky-500/40 to-indigo-400/10", chip: "text-sky-300", ring: "ring-sky-400/30" },
];

export default function Benefits({ onOpenLead }) {
  const { lang } = useLang();
  const c = COPY[lang];
  const [active, setActive] = useState(null);

  return (
    <section
      className="relative py-20 md:py-28 bg-[#050814] text-white overflow-hidden"
      data-testid="benefits-section"
    >
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
      <div className="absolute -top-32 left-1/3 w-[36rem] h-[36rem] rounded-full bg-blue-600/20 blur-3xl" />
      <div className="absolute bottom-0 -right-40 w-[36rem] h-[36rem] rounded-full bg-violet-600/20 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-400">
            {c.eyebrow}
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
            {c.title}
          </h2>
          <p className="text-slate-300 mt-4 text-base md:text-lg max-w-2xl">
            {c.subtitle}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {c.cards.map((card, i) => {
            const Icon = ICONS[i];
            const tint = TINTS[i];
            const isActive = active === i;
            return (
              <motion.button
                key={i}
                type="button"
                onClick={onOpenLead}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.06, duration: 0.55, ease: "easeOut" }}
                whileHover={{ y: -6 }}
                className={`group relative text-left rounded-3xl p-7 md:p-8 border border-white/10 bg-white/[0.03] backdrop-blur-md overflow-hidden transition-all duration-500 ${
                  isActive ? "ring-1 " + tint.ring : ""
                }`}
                data-testid={`benefit-card-${i}`}
              >
                {/* animated gradient glow */}
                <div
                  className={`pointer-events-none absolute -inset-px rounded-3xl bg-gradient-to-br ${tint.glow} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                  style={{ filter: "blur(28px)" }}
                />
                {/* inner ring of light */}
                <div
                  className="pointer-events-none absolute -top-1/2 left-1/2 -translate-x-1/2 w-[140%] h-[140%] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                  style={{
                    background:
                      "radial-gradient(closest-side, rgba(255,255,255,0.10), transparent 70%)",
                  }}
                />

                <div className="relative flex items-start justify-between gap-4">
                  <motion.div
                    animate={isActive ? { rotate: [0, -6, 0], scale: [1, 1.06, 1] } : {}}
                    transition={{ duration: 0.6 }}
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br ${tint.glow} border border-white/10`}
                  >
                    <Icon className={`w-5 h-5 ${tint.chip}`} strokeWidth={2.2} />
                  </motion.div>

                  <div className="text-right">
                    <div className={`font-display text-3xl md:text-4xl font-bold tracking-tight ${tint.chip}`}>
                      {card.m}
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mt-1 max-w-[140px]">
                      {card.ms}
                    </div>
                  </div>
                </div>

                <h3 className="relative font-display text-xl md:text-2xl font-bold mt-7">
                  {card.t}
                </h3>
                <p className="relative text-slate-300 mt-2 text-sm leading-relaxed">
                  {card.d}
                </p>

                <div className="relative mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-white group-hover:gap-2.5 transition-all">
                  <span>Get this for my business</span>
                  <ArrowUpRight className="w-4 h-4" />
                </div>

                {/* subtle bottom shine */}
                <div className="absolute inset-x-6 bottom-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition" />
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
